import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../lib/cn.js";

export interface SegmentedControlProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  /** `icon`: a 14px Lucide icon shown before the label. */
  options: ReadonlyArray<{ value: T; label: string; icon?: ReactNode }>;
  /**
   * `strong`: the selected choice is filled in `primary`, for a mode that must be obvious at
   * a glance (the canvas tool, spec13). Default: the subtle `accent`.
   */
  variant?: "default" | "strong";
  id?: string;
  className?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

/**
 * A row of joined square buttons for 2–4 fixed choices (DESIGN.md: Segmented control).
 * Radio-group semantics: one tab stop, arrow keys move and select.
 */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  id,
  className,
  variant = "default",
  ...aria
}: SegmentedControlProps<T>) {
  return (
    <RadioGroupPrimitive.Root
      id={id}
      value={value}
      // Radix types the value as string; it only ever comes from `options`.
      onValueChange={(next) => onValueChange(next as T)}
      orientation="horizontal"
      className={cn("inline-flex w-fit border border-input", className)}
      {...aria}
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          className={cn(
            "touch-target relative inline-flex h-8 items-center gap-1.5 border-l border-input px-3 text-xs font-medium text-muted-foreground transition-colors duration-150 first:border-l-0 hover:bg-muted hover:text-secondary-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring [&_svg]:size-3.5 [&_svg]:shrink-0",
            variant === "strong"
              ? "data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:hover:bg-primary-hover"
              : "data-[state=checked]:bg-accent data-[state=checked]:text-accent-foreground",
          )}
        >
          {option.icon && <span aria-hidden>{option.icon}</span>}
          {option.label}
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}
